import type { SxProps, Theme, TypographyProps as MuiTypographyProps } from '@mui/material';
// Allow a typography import here due to it being the originating definition
// eslint-disable-next-line no-restricted-imports
import { Typography as MuiTypography } from '@mui/material';
import { forwardRef, type ReactNode } from 'react';

interface TypographyProps extends Omit<MuiTypographyProps, 'variant' | 'color'> {
  children?: ReactNode;
  sx?: SxProps<Theme>;
}

interface OverrideProps extends TypographyProps {
  variant: NonNullable<MuiTypographyProps['variant']>;
}

const withDefaults = (defaults: SxProps<Theme>, sx?: MuiTypographyProps['sx']): SxProps<Theme> => {
  if (!sx) return defaults;
  return [defaults, ...(Array.isArray(sx) ? sx : [sx])];
};

/** Top-level page heading. Use once per page for the primary header title. */
export const PageTitle = forwardRef<HTMLElement, TypographyProps>(
  ({ children, sx, ...props }, ref) => (
    <MuiTypography
      ref={ref}
      variant="h4"
      {...props}
      sx={withDefaults({ fontSize: '1.6rem', fontWeight: 500 }, sx)}
    >
      {children}
    </MuiTypography>
  ),
);

/** Primary in-page section heading. Use to introduce major content sections within a page. */
export const Heading = forwardRef<HTMLElement, TypographyProps>(
  ({ children, sx, ...props }, ref) => (
    <MuiTypography ref={ref} variant="h5" {...props} sx={sx}>
      {children}
    </MuiTypography>
  ),
);

/** Card and sub-section title. The most common heading — use for card headers, panel titles, and grouped content labels. */
export const Subheading = forwardRef<HTMLElement, TypographyProps>(
  ({ children, sx, ...props }, ref) => (
    <MuiTypography ref={ref} variant="h6" {...props} sx={sx}>
      {children}
    </MuiTypography>
  ),
);

/** Semantic label for a content group or form section. */
export const Title = forwardRef<HTMLElement, TypographyProps>(({ children, sx, ...props }, ref) => (
  <MuiTypography
    ref={ref}
    variant="subtitle1"
    {...props}
    sx={withDefaults({ fontWeight: 600 }, sx)}
  >
    {children}
  </MuiTypography>
));

/** Primary paragraph and content text. Use for descriptive text or multi-line content. */
export const Body = forwardRef<HTMLElement, TypographyProps>(({ children, sx, ...props }, ref) => (
  <MuiTypography ref={ref} variant="body1" {...props} sx={sx}>
    {children}
  </MuiTypography>
));

/** Bold secondary text for inline emphasis. Bold by default */
export const BodyEmphasised = forwardRef<HTMLElement, TypographyProps>(
  ({ children, sx, ...props }, ref) => (
    <MuiTypography ref={ref} variant="body2" {...props} sx={withDefaults({ fontWeight: 600 }, sx)}>
      {children}
    </MuiTypography>
  ),
);

/** Supporting text beneath a heading or label. */
export const Subtitle = forwardRef<HTMLElement, TypographyProps>(
  ({ children, sx, ...props }, ref) => (
    <MuiTypography
      ref={ref}
      variant="body2"
      {...props}
      sx={withDefaults({ color: 'text.secondary' }, sx)}
    >
      {children}
    </MuiTypography>
  ),
);

/** Timestamps, hints, and metadata labels. */
export const Caption = forwardRef<HTMLSpanElement, TypographyProps>(
  ({ children, sx, ...props }, ref) => (
    <MuiTypography
      ref={ref}
      variant="caption"
      {...props}
      sx={withDefaults({ color: 'text.secondary' }, sx)}
    >
      {children}
    </MuiTypography>
  ),
);

/** An override for one-off typography that doesn't fit the semantic set. Requires an explicit variant and should be used sparingly.
 * @variant Required: A mui defined component variant (e.g. "h1", "body2", "caption").
 * */
export const Override = forwardRef<HTMLElement, OverrideProps>(
  ({ variant, children, sx, ...props }, ref) => (
    <MuiTypography ref={ref} variant={variant} {...props} sx={sx}>
      {children}
    </MuiTypography>
  ),
);

// eslint-disable-next-line react-refresh/only-export-components
export const Typography = {
  PageTitle,
  Heading,
  Subheading,
  Title,
  Body,
  BodyEmphasised,
  Subtitle,
  Caption,
  Override,
};
